<script>
	import { nanoid } from "nanoid";
	import { tick } from "svelte";
	import {
		getCurrencySymbol,
		getHighLowRestriction,
		parseCurrency
	} from "../data/transactionHighLowRestrictions";

	export let id = nanoid(6);
	export let label = null;
	export let sublabel = null;
	export let value = null;
	export let disabled = null;
	export let readonly = null;
	export let required = null;
	export let placeholder = null;
	export let enforceHighLowRestrictions = false;
	export let validatorMessage = null;
	export let currency = 'AUD';
	export let stockCode = null;
	export let transactionDate = '2025-03-24';
	export let hasPriceError = false;

	let input;
	let previousCurrency = currency;
	let previousStockCode = stockCode;
	

	// The Datepicker emits dates as dd/mm/yyyy
	const normaliseDate = (d) => {
		if (!d) return d;
		if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
		const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(d);
		return match ? `${match[3]}-${match[2]}-${match[1]}` : d;
	};

	$: lookupDate = normaliseDate(transactionDate);
	$: parsedCurrency = parseCurrency(currency);
	$: symbol = getCurrencySymbol(parsedCurrency);
	$: currencyRestriction = getHighLowRestriction(lookupDate, parsedCurrency);

	$: restriction = enforceHighLowRestrictions
		? stockCode === "JHX:NYSE"
			? { highLimit: 100, lowLimit: null }
			: stockCode === "JHX:ASX" && currencyRestriction.highLimit == null
				? { ...currencyRestriction, highLimit: 100 }
				: currencyRestriction
		: { highLimit: null, lowLimit: null };
		
	$: highLimit = restriction.highLimit;
	$: lowLimit = restriction.lowLimit;
	$: hasValue = value !== null && value !== undefined && `${value}`.trim() !== "";

	$: hasBounds = enforceHighLowRestrictions && (stockCode === "JHX:ASX" || stockCode === "JHX:NYSE") && (lowLimit !== null || highLimit !== null);
	$: rangeMessage = validatorMessage || "Please enter a valid value.";

	const validateCurrentValue = async () => {
		await tick();
		input?.dispatchEvent(new Event("change", { bubbles: true }));
	};

	$: if (input && enforceHighLowRestrictions && (currency !== previousCurrency || stockCode !== previousStockCode)) {
		previousCurrency = currency;
		previousStockCode = stockCode;
		if (hasValue)
		{
			validateCurrentValue();
		}
	}

	$: if (!hasValue && hasPriceError) {
		hasPriceError = false;
	}


	const handleKeyDown = (e) => {
		if (
			e.key == "Backspace" ||
			e.key == "Delete" ||
			e.key == "ArrowLeft" ||
			e.key == "ArrowRight" ||
			e.key === "Enter"
		) {
			return true;
		}
	};

	const handleKeyPress = (e) => {
		if (e.metaKey || e.altKey || e.ctrlKey || e.key === "Enter") {
			return true;
		}

		if (e.key.match(/\d/) || e.key.match(/\./)) {
			const oldValue = value ? value.toString() : null;

			if (
				!(
					oldValue &&
					oldValue.includes &&
					oldValue.includes(".") &&
					oldValue.indexOf(".") === oldValue.length - 3
				)
			) {
				return true;
			}
		}

		e.preventDefault();
		return false;
	};
</script>

{#if label}
	<label for={id}>
		{label}

		{#if required}
			<span class="required">*</span>
		{/if}

		{#if sublabel}
			<div class="sublabel">{sublabel}</div>
		{/if}
	</label>
{/if}
<div class="currency-group">
	<span class="symbol">{symbol}</span>
	<input
		{required}
		{disabled}
		{readonly}
		{id}
		step="any"
		bind:value
		type="number"
		class="form-control"
		inputmode="numeric"
		bind:this={input}
		{placeholder}
		data-validator={hasBounds && hasValue ? "currencyRange" : null}
		data-min={lowLimit}
		data-max={highLimit}
		data-bouncer-message={rangeMessage}
		data-bouncer-target=".decimal-error"
		on:bouncerShowError={() => (hasPriceError = hasValue)}
		on:bouncerRemoveError={() => (hasPriceError = false)}
		on:keydown={handleKeyDown}
		on:keypress={handleKeyPress} />
	<div class="decimal-error"></div>
</div>

<style>
	input {
		padding-left: 26px;
	}

	.currency-group {
		position: relative;
		width: 100%;
	}

	.currency-group .symbol {
		position: absolute;
		line-height: 50px;
		left: 10px;
	}
</style>
